import {
  HbrCheckbox,
  HbrDivider,
  HbrForm,
  HbrInput,
  HbrOption,
  HbrSelect,
  HbrTextarea,
} from '@harbor/elements-react-standalone';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useParams } from 'react-router-dom';
import {
  useFetchAllAccountSegment,
  useFetchAllAccountType,
  useFetchAllGeo,
  useFetchAllMarketSegment,
  useFetchPartner,
} from '../../hooks/mssp-api-hooks';
import { putItem } from '../../support/queries';
import { asMultiValue } from '../../support/utils';
import { CacheKey } from '../../types/cache-keys';
import { Partner } from '../../types/msp';
import StbButton from '../security-toolbox-ui/StbButton';


const PartnerComponent: React.FC<{ isEditing?: boolean }> = ( { isEditing = false }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [initialPartner, setInitialPartner] = useState<Partner | null>(null);
  const [currentPartner, setCurrentPartner] = useState<Partner | null>(null);
  const [isEditingState, setIsEditingState] = useState(false);
  const [marketSegmentTagValue, setMarketSegmentTagValue] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const { data: partner, isLoading: isLoadingPartner } = useFetchPartner(Number(id));
  const { data: geo, isLoading: isLoadingGeo } = useFetchAllGeo();
  const { data: accountSegments, isLoading: isLoadingAccountSegments } = useFetchAllAccountSegment();
  const { data: accountTypes, isLoading: isLoadingAccountTypes } = useFetchAllAccountType();
  const { data: marketSegments, isLoading: isLoadingMarketSegments } = useFetchAllMarketSegment();

  useEffect(() => {
    setIsEditingState(isEditing);
  }, [isEditing]);

  useEffect(() => {
    if (partner) {
      if (!initialPartner) {
        setInitialPartner(partner);
      }
      if (!currentPartner) {
        setCurrentPartner(partner);
      }
      setMarketSegmentTagValue(partner?.market_segment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner]);

  const updateMutation = useMutation<
    Omit<Partner, 'market_segment'> & { market_segment: number[] },
    Error,
    Omit<Partner, 'market_segment'> & { market_segment: number[] },
    {
      previous: Partner | undefined;
      singleExists: boolean;
      queryKey: string[];
    }
  >({
    mutationFn: (updatedPartner) => putItem('db/partner', updatedPartner),
    onMutate: async (updatedPartner) => {
      const queryKey = [CacheKey.Partner, String(updatedPartner.id)];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Partner>(queryKey) || undefined;
      const singleExists = queryClient.getQueryData<Partner>(queryKey) !== undefined;
      const data = {
        ...updatedPartner,
        market_segment: updatedPartner.market_segment.map((id) =>
          marketSegments.find((segment) => segment.id === id)
        ),
      };

      if (singleExists) {
        queryClient.setQueryData<Partner>(queryKey, (prev) => ({ ...prev, ...data }));
      }
      return { previous, singleExists, queryKey };
    },
    onSuccess: (response, _variables, context) => {
      const data = {
        ...response,
        market_segment: response.market_segment.map((id) =>
          marketSegments.find((segment) => segment.id === id)
        ),
      };
      if (context.singleExists) {
        queryClient.setQueryData<Partner>(context.queryKey, data);
      }
      alert('Partner updated successfully!');
      setIsEditingState(false);
      setLoading(false);
    },
    onError: (_error, _variables, context) => {
      if (context.singleExists && context.previous) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
      console.error('Error updating the partner!', _error);
      setLoading(false);
    },
    onSettled: (_response, _error, _variables, context) => {
      queryClient.invalidateQueries({ queryKey: context.queryKey });
    },
  });

  const handleSavePartner = () => {
    if (!currentPartner || !marketSegments) return;

    setLoading(true);

    updateMutation.mutate({
      ...currentPartner,
      market_segment: marketSegmentTagValue,
    });
    setInitialPartner(currentPartner);
  };

  const handleSetEditing = () => {
    setIsEditingState(true);
  };

  const handleCancelEditing = () => {
    setCurrentPartner(initialPartner ?? null); // Reset to the initial state
    setIsEditingState(false);
  };

  const handleInputChange = (field: keyof Partner, value: any) => {
    setCurrentPartner((prev) => {
      if (!prev) return null;

      let updatedValue: any;

      switch (field) {
        case 'geo': {
          updatedValue = Number(value);
          break;
        }
        case 'account_type': {
          updatedValue = Number(value);
          break;
        }
        case 'account_segment': {
          updatedValue = Number(value);
          break;
        }
        default: {
          updatedValue = value;
          break;
        }
      }

      return {
        ...prev,
        [field]: updatedValue,
      };
    });
  };

  if (!currentPartner || !geo || !accountSegments || !accountTypes || !marketSegments) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div style={{ float: 'right' }}>
        {!isEditingState ? (
          <StbButton role="update" disabled={loading} onClick={handleSetEditing}>Edit</StbButton>
        ) : (
          <>
            <StbButton role="cancel" onClick={handleCancelEditing} disabled={loading} className="mr-2" />
            <StbButton
              role="save"
              onClick={handleSavePartner}
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </StbButton>
          </>
        )}
      </div>
      <br />
      {loading && <p>Loading...</p>} {/* Display a loading message */}
      <HbrForm>
        <HbrInput
          label="Name"
          value={currentPartner?.name || ''}
          disabled={!isEditingState || loading}
          onHbr-input={(e) => handleInputChange('name', e.target.value)}
        ></HbrInput>

        <HbrInput
          label="Managed / Resale"
          value={currentPartner?.managed_resale || ''}
          disabled={!isEditingState || loading}
          onHbr-input={(e) => handleInputChange('managed_resale', e.target.value)}
        ></HbrInput>

        <HbrTextarea
          label="Competitive Landscape"
          value={currentPartner?.competitive_landscape || ''}
          disabled={!isEditingState || loading}
          onHbr-input={(e) => handleInputChange('competitive_landscape', e.target.value)}
        ></HbrTextarea>

        <HbrSelect
          label="Account Type"
          placeholder={
            currentPartner?.account_type
              ? accountTypes?.find((type) => type.id === currentPartner.account_type)
              ?.name || 'Select Account Type'
              : 'Select Account Type'
          }
          disabled={!isEditingState || loading}
          onHbr-change={(e) => handleInputChange('account_type', e.target.value)}
        >
          {accountTypes?.map((accountType) => (
            <HbrOption key={accountType.id} value={accountType.id}>
              {accountType.name}
            </HbrOption>
          ))}
        </HbrSelect>

        <HbrSelect
          label="Account Segment"
          placeholder={
            currentPartner?.account_segment
              ? accountSegments?.find((segment) => segment.id === currentPartner.account_segment)?.name || 'Select Account Segment'
              : 'Select Account Segment'
          }
          disabled={!isEditingState || loading}
          onHbr-change={(e) => handleInputChange('account_segment', e.target.value)}
        >
          {accountSegments?.map((segment) => (
            <HbrOption key={segment.id} value={segment.id}>
              {segment.name}
            </HbrOption>
          ))}
        </HbrSelect>

        <HbrSelect
          label="Geo"
          placeholder={
            currentPartner?.geo
              ? geo?.find((g) => g.id === currentPartner.geo)?.name || 'Select Geo'
              : 'Select Geo'
          }
          disabled={!isEditingState || loading}
          onHbr-change={(e) => handleInputChange('geo', e.target.value)}
        >
          {geo?.map((g) => (
            <HbrOption key={g.id} value={g.id}>
              {g.name}
            </HbrOption>
          ))}
        </HbrSelect>

        <HbrSelect
          label="Market Segments"
          variant={'tag'}
          value={marketSegmentTagValue.map(String)}
          onHbr-change={(e) =>
            setMarketSegmentTagValue(asMultiValue(e.target.value).map(Number))
          }
          style={{ width: '200px' }}
          disabled={!isEditingState || loading}
        >
          {marketSegments?.map((segment) => (
            <HbrOption key={segment.id} value={String(segment.id)}>
              <HbrCheckbox
                onHbr-change={(e) => e.stopPropagation()}
                checked={marketSegmentTagValue.includes(segment.id)}
              >
                {segment.name}
              </HbrCheckbox>
            </HbrOption>
          ))}
        </HbrSelect>

        <HbrDivider
          prominence="strong"
          style={{
            marginTop: 'var(--hbr-spacing-xl)',
            marginBottom: 'var(--hbr-spacing-lg)',
          }}
        />
        <h4>Cisco Roles</h4>
        <br />

        <HbrInput
          label="Executive Sponsor"
          value={currentPartner?.cisco_exec_sponsor || ''}
          disabled={!isEditingState || loading}
          onHbr-input={(e) => handleInputChange('cisco_exec_sponsor', e.target.value)}
        ></HbrInput>

        <HbrInput
          label="GSSO Business Development Lead"
          value={currentPartner?.gsso_biz_dev_lead || ''}
          disabled={!isEditingState || loading}
          onHbr-input={(e) => handleInputChange('gsso_biz_dev_lead', e.target.value)}
        ></HbrInput>

        <HbrInput
          label="GSSO Technical Lead"
          value={currentPartner?.gsso_tech_lead || ''}
          disabled={!isEditingState || loading}
          onHbr-input={(e) => handleInputChange('gsso_tech_lead', e.target.value)}
        ></HbrInput>

        <HbrDivider
          prominence="strong"
          style={{
            marginTop: 'var(--hbr-spacing-xl)',
            marginBottom: 'var(--hbr-spacing-lg)',
          }}
        />
        <h4>Partner Roles</h4>
        <br />

        <HbrInput
          label="Executive Sponsor"
          value={currentPartner?.partner_exec_sponsor || ''}
          disabled={!isEditingState || loading}
          onHbr-input={(e) => handleInputChange('partner_exec_sponsor', e.target.value)}
        ></HbrInput>

        <HbrInput
          label="Business Development Lead"
          value={currentPartner?.gprs_biz_dev_lead || ''}
          disabled={!isEditingState || loading}
          onHbr-input={(e) => handleInputChange('gprs_biz_dev_lead', e.target.value)}
        ></HbrInput>

        <HbrInput
          label="Sales Acceleration Lead"
          value={currentPartner?.gprs_sales_accel_pbdm || ''}
          disabled={!isEditingState || loading}
          onHbr-input={(e) => handleInputChange('gprs_sales_accel_pbdm', e.target.value)}
        ></HbrInput>
      </HbrForm>
    </div>
  );
};

export default PartnerComponent;